import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { mixpanel, links, constants, snowplow } from 'common/helpers'


const setDate = (date) => reducers.view.giftCardPage.setDate(date)

const setPrice = (price) => reducers.view.giftCardPage.setPrice(price)

const setDeliveryType = (type) => reducers.view.giftCardPage.setDeliveryType(type)

const setSelectedSkinId = (skinId) => reducers.view.giftCardPage.setSelectedSkinId(skinId)

/**
 *
 * @param {object} data
 * @param {string} data.name
 * @param {string} data.email
 * @param {string} data.message
 */
const setPerson = (data) => reducers.view.giftCardPage.setPerson(data)

const reset = reducers.view.giftCardPage.reset


// Fetch methods ---------------------------------------------------------- /

const fetchCardSkins = () => {
  reducers.view.giftCardPage.setCardSkinsFetching(true)

  request.get('rest/gift/card/styles')
    .then((response) => {
      reducers.view.giftCardPage.setCardSkins(response)
    }, () => {
      reducers.view.giftCardPage.setCardSkinsFetching(false)
    })
}


// Modals flow ------------------------------------------------------------ /

// helpers

const openModalWithData = (modalName, modalData) => {
  actions.models.modals.open(modalName, modalData)
}

const finishFlowAndGoToPayment = () => {
  const {
    view: {
      giftCardPage: {
        // TODO: When the page is added, you need to rename
        // props in the localStorage on the GiftCardPaymentPage
        selectedSkinId: cardStyleId, deliveryType: sendBy,
        price, date, email, name, gender, message,
      },
    },
  } = getState()

  mixpanel.track('Gift time', {
    time: date ? 'calendar' : 'rightaway',
    'gift type': date ? 'calendar' : 'instant',
  })

  actions.misc.cookies.setItem(
    constants.cookieNames.paymentGiftCard,
    { cardStyleId, price, date, email, name, gender, message, sendBy }
  )

  actions.models.router.push(links.giftCardPayment)
}


// modals data objects
/**
 * @description:
 * Open choose gift card value modal (first modal in the flow).
 */
const openChooseGiftCardValueModal = () =>
  openModalWithData(constants.modals.gifts.ChooseGiftCardValue, {
    onNext: (price) => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'choose value',
        step: 1,
        placement: 'modal',
        value: Number(price),
      }, false)

      startGetGiftCardFlowInSecondModal(price)
    },
  })

/**
 * @description:
 * Open choose gift card skin modal (second modal in the flow).
 */
const openChooseGiftCardSkinModal = () =>
  openModalWithData(constants.modals.gifts.ChooseGiftCardSkin, {
    onNext: (selectedSkin) => {
      const { id } = selectedSkin

      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'choose design',
        step: 2,
        styleId: id,
      }, false)

      openChooseGiftCardDeliveryTypeModal()
      actions.view.giftCardPage.setSelectedSkinId(id)
    },
    onBack: openChooseGiftCardValueModal,
  })

/**
 * @description:
 * Open choose gift card delivery type modal (third modal in the flow).
 */
const openChooseGiftCardDeliveryTypeModal = () =>
  openModalWithData(constants.modals.gifts.ChooseGiftCardDeliveryType, {
    onChoose: (deliveryType) => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'delivery method',
        step: 3,
        method: deliveryType,
      }, false)

      setDeliveryType(deliveryType)

      if (deliveryType === constants.deliveryTypes.mail) {
        finishFlowAndGoToPayment()
      }
      else {
        openSetGiftRecipientInfoModal()
      }
    },
    onBack: openChooseGiftCardSkinModal,
  })

/**
 * @description:
 * Open set gift card recipient info modal (fourth modal in the flow).
 */
const openSetGiftRecipientInfoModal = () =>
  openModalWithData(constants.modals.gifts.SetGiftRecipientInfo, {
    onNext: ({ name, email, message }) => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'recipient',
        step: 4,
        email,
      }, false)

      mixpanel.track('Gift email and name')
      setPerson({ name, email, message })

      openSetGiftDateModal()
    },
    onBack: openChooseGiftCardDeliveryTypeModal,
  })

/**
 * @description:
 * Open set gift card date modal (fifth modal in the flow).
 */
const openSetGiftDateModal = () => {
  openModalWithData(constants.modals.gifts.SetGiftDate, {
    onNext: () => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'choose date',
        step: 5,
        when: 'now',
      }, false)

      finishFlowAndGoToPayment()
    },
    onBack: () => openSetGiftRecipientInfoModal(),
    onChooseSpecificDate: () => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'choose date',
        step: 5,
        when: 'specific',
      }, false)

      openPickDateModal()
    },
  })
}

/**
 * @description:
 * Open pick gift card date modal (sixth modal in the flow).
 */
const openPickDateModal = () => {
  openModalWithData(constants.modals.PickDate, {
    onNext: () => {
      snowplow.track(constants.snowplowEvents.buyGiftCardStepComplete, {
        stepName: 'choose specific date',
        step: 6,
      }, false)

      finishFlowAndGoToPayment()
    },
    onBack: () => openSetGiftDateModal(),
    onPickDate: (date) => setDate(date),
  })
}

/**
 * @description:
 * This function starts the flow. We have a lot of abstract modals.
 * It is necessary to open them in turn. For each modal window, the parameters are specified.
 * @param {function} onNext - closes an open modal window, does some actions
 * and opens a new modal window, or does a redirect
 * @param {function} onBack - closes the open modal window, opens the previous window
 * ... Modal windows can also use additional parameters when opening
 */
const startGetGiftCardFlow = openChooseGiftCardValueModal

const startGetGiftCardFlowInSecondModal = (price) => {
  setPrice(price)
  openChooseGiftCardSkinModal()
}


export default {
  startGetGiftCardFlowInSecondModal,
  startGetGiftCardFlow,
  setSelectedSkinId,
  fetchCardSkins,
  reset,
}
